import { useState, useEffect, useCallback, useMemo } from 'react';
import walletApi from '../../../api/wallet';
import { getErrorMessage } from '../../../utils/errors';

const calculateSavingsRate = (cashIn, cashOut) => {
  if (cashIn <= 0) return 0;
  return ((cashIn - cashOut) / cashIn) * 100;
};

const calculatePercentChange = (current, previous) => {
  if (previous <= 0) return current > 0 ? 100 : 0;
  return ((current - previous) / previous) * 100;
};

const useBalanceData = ({ viewMode, months, currency }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rawBalance, setRawBalance] = useState([]);
  const [summary, setSummary] = useState(null);
  const [positions, setPositions] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const [balanceRes, summaryRes, positionsRes] = await Promise.all([
        walletApi.get(`/balance?months=${months}&viewMode=${viewMode === 'annual' ? 'annual' : 'monthly'}`),
        walletApi.get('/home/summary?days=31'),
        walletApi.get('/positions/asset-class'),
      ]);

      setRawBalance(balanceRes.data || []);
      setSummary(summaryRes.data || null);
      setPositions(positionsRes.data || null);
    } catch (err) {
      setError(getErrorMessage(err, 'Erro ao carregar dados financeiros'));
    } finally {
      setLoading(false);
    }
  }, [viewMode, months]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const processedData = useMemo(() => {
    if (!rawBalance.length) {
      return {
        currentPeriod: null,
        previousPeriod: null,
        tableData: [],
        kpis: null,
        comparison: null,
      };
    }

    const tableData = rawBalance.map((item) => {
      const cashIn = parseFloat(item.balance?.cash_in?.[currency] || 0);
      const cashOut = parseFloat(item.balance?.cash_out?.[currency] || 0);
      const netBalance = cashIn - cashOut;
      const savingsRate = calculateSavingsRate(cashIn, cashOut);

      return {
        period: item.year_month,
        cashIn,
        cashOut,
        netBalance,
        savingsRate,
        percentageDifference: parseFloat(item.percentage_difference?.[currency] || 0),
      };
    });

    const currentPeriod = tableData[0] || null;
    const previousPeriod = tableData[1] || null;

    let comparison = null;
    if (currentPeriod && previousPeriod) {
      comparison = {
        cashInChange: calculatePercentChange(currentPeriod.cashIn, previousPeriod.cashIn),
        cashOutChange: calculatePercentChange(currentPeriod.cashOut, previousPeriod.cashOut),
        netBalanceChange: currentPeriod.netBalance - previousPeriod.netBalance,
        savingsRateChange: currentPeriod.savingsRate - previousPeriod.savingsRate,
      };
    }

    let kpis = null;
    if (currentPeriod) {
      kpis = {
        cashIn: currentPeriod.cashIn,
        cashOut: currentPeriod.cashOut,
        netBalance: currentPeriod.netBalance,
        savingsRate: currentPeriod.savingsRate,
        cashInChange: comparison?.cashInChange || 0,
        cashOutChange: comparison?.cashOutChange || 0,
        netBalanceChange: comparison?.netBalanceChange || 0,
        savingsRateChange: comparison?.savingsRateChange || 0,
      };
    }

    return {
      currentPeriod,
      previousPeriod,
      tableData,
      kpis,
      comparison,
    };
  }, [rawBalance, currency]);

  const wealthData = useMemo(() => {
    if (!positions?.Totals) return null;

    const totals = positions.Totals[0] || positions.Totals;
    const totalInvested = parseFloat(totals?.TotalInvested?.[currency] || 0);
    const currentValue = parseFloat(totals?.CurrentValue?.[currency] || 0);
    const profitLoss = currentValue - totalInvested;
    const profitLossPercentage = totalInvested > 0 ? (profitLoss / totalInvested) * 100 : 0;

    return {
      totalInvested,
      currentValue,
      profitLoss,
      profitLossPercentage,
    };
  }, [positions, currency]);

  return {
    loading,
    error,
    rawBalance,
    summary,
    positions,
    ...processedData,
    wealthData,
    refetch: fetchData,
  };
};

export default useBalanceData;
